/**
 * Recovery for webpack chunk load failures.
 *
 * Every route in this app is a `lazy()` import, so it ships as a
 * content-hashed chunk (`static/js/4.<hash>.chunk.js`). `deploy.sh` rebuilds
 * in place, which deletes the previous build's chunks. A tab that was open
 * before the deploy is still running the old runtime and asks for the old
 * filename - the request 404s (or, because of the SPA fallback in
 * `redirects/nginx.conf`, returns index.html with a 200 that webpack cannot
 * execute) and the dynamic import rejects with a ChunkLoadError.
 *
 * The fix is a single reload: index.html is served `no-store`, so the fresh
 * copy points at the new hashes and the user lands back on their route.
 */

const RELOAD_MARKER = "rimagic:chunk-reload-at";

/**
 * A reload that does not fix the error means the deployed build itself is
 * broken. Refusing to reload again within this window keeps the user out of
 * a refresh loop.
 */
const RELOAD_COOLDOWN_MS = 30_000;

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk \S+ failed/i,
  /Loading CSS chunk \S+ failed/i,
  /Failed to fetch dynamically imported module/i,
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const candidate = error as { name?: string; code?: string; message?: string };
  if (candidate.name === "ChunkLoadError") return true;
  if (candidate.code === "CSS_CHUNK_LOAD_FAILED") return true;

  const message = candidate.message;
  return (
    typeof message === "string" &&
    CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
  );
}

function getStorage(): Storage | null {
  const candidates = [
    () => window.sessionStorage,
    () => window.localStorage,
  ];

  for (const getCandidate of candidates) {
    try {
      const storage = getCandidate();
      const probe = `${RELOAD_MARKER}:probe`;
      storage.setItem(probe, "1");
      storage.removeItem(probe);
      return storage;
    } catch {
      // Storage blocked (private mode, cookies disabled) - try the next one.
    }
  }

  return null;
}

/**
 * Records a reload attempt and reports whether it is safe to go ahead.
 *
 * Returns false when we already reloaded moments ago, or when no storage is
 * writable - without somewhere to keep the marker there is no way to break a
 * refresh loop, so we leave the user on the error screen with a manual
 * reload button instead.
 */
export function markChunkReloadAttempt(): boolean {
  const storage = getStorage();
  if (!storage) return false;

  const lastReload = Number(storage.getItem(RELOAD_MARKER));
  if (lastReload && Date.now() - lastReload < RELOAD_COOLDOWN_MS) return false;

  storage.setItem(RELOAD_MARKER, String(Date.now()));
  return true;
}

export function reloadForNewBuild(): void {
  window.location.reload();
}

/**
 * Catches chunk failures that never reach the error boundary, e.g. a route
 * prefetch or an import fired from an event handler.
 */
export function installChunkErrorHandler(): void {
  window.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadError(event.reason)) return;
    if (!markChunkReloadAttempt()) return;

    // Handled - keep it out of the console and out of BugCatch.
    event.preventDefault();
    reloadForNewBuild();
  });
}
